import { useEffect, useRef, useState } from "react";

import { css } from "@compiled/react";
import { MessageDescriptor } from "react-intl";

import { AtlassianIcon } from "@atlaskit/logo";

import { ufoEventRedirectToProduct } from "@/components/signup/utils";

import { ErrorIcon } from "./error-icon";
import { Message } from "./message.tsx";
import { getProgressAmount } from "./util";

const B200 = "#2684ff";
const N0 = "#ffffff";
const N30 = "#ebecf0";
const R300 = "#ff5630";
const Y300 = "#ffab00";
const size = 175;
const barWidth = 8;
export const delayInMs = 500;

const circleWrapCss = css({
    "backgroundColor": N30,
    "borderRadius": "50%",
    "height": `${size}px`,
    "width": `${size}px`,

    ".mask, .fill": {
        borderRadius: "50%",
        height: `${size}px`,
        position: "absolute",
        width: `${size}px`
    },

    ".mask": {
        clip: `rect(0px, ${size}px, ${size}px, ${size / 2}px)`
    },

    ".fill": {
        clip: `rect(0px, ${size / 2}px, ${size}px, 0px)`,
        backgroundColor: B200
    },

    ".timeout .fill": {
        backgroundColor: Y300
    },

    ".error .fill": {
        backgroundColor: R300
    },

    ".inside-circle": {
        backgroundColor: N0,
        borderRadius: "50%",
        color: B200,
        height: `${size - barWidth * 2}px`,
        margin: `${barWidth}px`,
        position: "absolute",
        width: `${size - barWidth * 2}px`,

        span: {
            display: "flex !important",
            justifyContent: "center",
            alignItems: "center",
            width: "100%",
            height: "100%",

            svg: {
                width: `${size / 2}px`,
                height: `${size / 2}px`
            }
        }
    },

    "@media (prefers-reduced-motion: no-preference)": {
        ".mask.full, .fill": {
            transition: `background-color ${delayInMs}ms, transform ${delayInMs}ms`
        }
    }
});

const containerStyleCss = css({
    alignItems: "center",
    display: "flex",
    flexDirection: "column",
    justifyContent: "center"
});

export type ProgressCircleProps = {
    startTime: number;
    status?: "timeout" | "error" | "success";
    message: MessageDescriptor;
};

export const ProgressCircle = ({
    startTime,
    status,
    message
}: ProgressCircleProps) => {
    // progress is a number 0 to 1
    const [progress, setProgress] = useState<number>(
        status ? 1 : getProgressAmount(startTime)
    );
    const interval = useRef<ReturnType<typeof setInterval> | null>(null);
    const rotation = { transform: `rotate(${progress * 180}deg)` };

    // start timer
    useEffect(() => {
        if (status) return;

        interval.current = setInterval(() => {
            const progressAmount = getProgressAmount(startTime);
            setProgress(progressAmount);
            if (interval.current && progressAmount === 1) {
                clearInterval(interval.current);
            }
        }, 100);

        return () => {
            if (interval.current) {
                clearInterval(interval.current);
            }
        };
    }, []);

    // don't show error icon before the bar reaches 100%
    const [delayedStatus, setDelayedStatus] = useState(status);
    useEffect(() => {
        let timeout: ReturnType<typeof setTimeout>;
        if (status && !delayedStatus) {
            if (interval.current) {
                clearInterval(interval.current);
            }
            setProgress(1);
            timeout = setTimeout(() => {
                setDelayedStatus(status);
            }, delayInMs);
        } else {
            setDelayedStatus(status);
        }
        return () => clearTimeout(timeout);
    }, [status]);

    /**
     * UFOs for Signup's FE Observability & "Product Landing" SLOs
     *   - https://go/signup-ufo
     *   - https://go/signup-slo
     **/
    useEffect(() => {
        if (delayedStatus === "error") {
            // SLO "bad event" for redirect into product
            ufoEventRedirectToProduct.failure({
                customData: {
                    reactComponent: "ProgressCircle",
                    analyticsEvent: "errored",
                    provisioningStatus: delayedStatus
                }
            });
        } else if (delayedStatus === "timeout") {
            // SLO "bad event" for redirect into product
            ufoEventRedirectToProduct.failure({
                customData: {
                    reactComponent: "ProgressCircle",
                    analyticsEvent: "timedOut",
                    provisioningStatus: delayedStatus
                }
            });
        }
    }, [delayedStatus]);

    const ariaProperties = status
        ? {}
        : {
              "aria-valuemax": 100,
              "aria-valuemin": 0,
              "aria-valuenow": progress * 100
          };
    return (
        <div
            {...ariaProperties}
            aria-busy={progress !== 1}
            aria-labelledby="progress-label"
            css={containerStyleCss}
            data-testid="progress-circle"
            role="progressbar"
        >
            <div css={circleWrapCss}>
                <div
                    className={
                        delayedStatus ? `circle ${delayedStatus}` : "circle"
                    }
                >
                    <div className="mask full" style={rotation}>
                        <div className="fill" style={rotation} />
                    </div>
                    <div className="mask half">
                        <div className="fill" style={rotation} />
                    </div>
                    <div className="inside-circle">
                        {delayedStatus === "error" ||
                        delayedStatus === "timeout" ? (
                            <ErrorIcon />
                        ) : (
                            <AtlassianIcon />
                        )}
                    </div>
                </div>
            </div>
            <Message message={message} />
        </div>
    );
};
